import React, { useEffect, useRef } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import LeftArrow from "@svg/LeftArrow";
import RiddlesHelpIcon from "@svg/RiddlesHelpIcon";
import RiddlesRewardsIcon from "@svg/RiddlesRewardsIcon";
import RiddlesTipIcon from "@svg/RiddlesTipIcon";
import { useAppContext } from "@context/app";
import { segmentEvent } from "@utils/segment";
import RamadanRiddlesCalendar from "../RamadanRiddlesCalendar";
import { useRiddlesNavigation, useRiddlesOverview } from "../RamadanRiddlesContext";
import RamadanRiddlesEidView from "./RamadanRiddlesEidView";
import RamadanRiddlesQuestionBanner from "./RamadanRiddlesQuestionBanner";

const RamadanRiddlesProgress: React.FC = () => {
    const t = useTranslations();
    const { locale } = useRouter();
    const { setView, closeDrawer } = useRiddlesNavigation();
    const { data: overviewData } = useRiddlesOverview();
    const { user, country, area, eventProperties } = useAppContext();
    const hasTrackedViewRef = useRef(false);

    const rewardsText =
        overviewData?.rewards_text ??
        t("Keep playing daily to unlock bigger rewards on day 7, 14, 21 & Eid");
    const streakDays = overviewData?.streak_days ?? 0;
    const ramadanDays = overviewData?.ramadan_days ?? [];
    const questionBanner = overviewData?.question_banner;
    const hasEnded = overviewData?.ramadan_has_ended;
    const language = locale?.includes("en") ? "English" : "Arabic";

    const handleStartQuestion = () => setView("question");

    useEffect(() => {
        if (!overviewData || hasTrackedViewRef.current) return;
        hasTrackedViewRef.current = true;

        segmentEvent("Ramadan Calendar Page Opened", {
            ...eventProperties,
            area_id: area?.id,
            area_slug: area?.slug,
            country_id: country?.id,
            country_slug: country?.slug,
            language,
            user_id: user?.data?.id || -1,
            is_ramadan_ended: Boolean(overviewData?.ramadan_has_ended),
            number_of_streak_days: String(overviewData?.streak_days ?? 0),
        });
    }, [
        area?.id,
        area?.slug,
        country?.id,
        country?.slug,
        eventProperties,
        language,
        overviewData,
        user?.data?.id,
    ]);

    if (hasEnded) {
        return <RamadanRiddlesEidView />;
    }

    return (
        <div className="flex h-full flex-col bg-[#F6EAD5]">
            <div className="flex-1">
                <div className="bg-[#4F2F18]">
                    <div className="relative">
                        <Image
                            src="/svgs/ramadan-riddle-beige-header.svg"
                            alt=""
                            aria-hidden="true"
                            width={495}
                            height={203}
                            sizes="100vw"
                            className="h-auto w-full"
                        />
                        <button
                            type="button"
                            onClick={closeDrawer}
                            className="absolute left-6 top-4 text-[#4F2F1A]"
                            aria-label={t("Back")}
                        >
                            <LeftArrow />
                        </button>
                    </div>

                    <div className="pb-6 pt-4 text-center text-white">
                        <p className="text-sm font-bold uppercase">{t("Your Ramadan Streak")}</p>
                        <div className="relative mt-4 flex w-full flex-col items-center">
                            <div className="relative flex h-[92px] w-full items-center justify-center">
                                <Image
                                    src="/svgs/ramadan-riddle-day-clouds.svg"
                                    alt=""
                                    aria-hidden="true"
                                    width={496}
                                    height={109}
                                    sizes="100vw"
                                    className="absolute left-0 right-0 mx-auto h-auto w-full max-w-none"
                                    style={{ width: "100%" }}
                                />
                                <span className="relative text-[64px] font-extrabold leading-none text-[#FCC500]">
                                    {streakDays}
                                </span>
                            </div>
                            <p className="mt-2 text-sm text-white">{t("days in a row")}</p>
                        </div>
                        <div className="mx-auto mb-4 mt-4 flex w-full max-w-[400px] items-center gap-3 rounded-xl bg-[#38190D] px-4 py-2 text-start text-sm text-white">
                            <RiddlesTipIcon className="h-8 w-8 text-[#FCC500]" />
                            <p className="whitespace-pre-line leading-5">{rewardsText}</p>
                        </div>
                    </div>
                </div>

                <div className="-mt-6 rounded-t-[32px] bg-[#F6EAD5] px-6 pb-6 pt-6">
                    <div className="text-center">
                        <h2 className="text-lg font-extrabold uppercase text-[#38190D]">
                            {t("30 Days of Dabdoob Delights")}
                        </h2>
                        <p className="mt-1 text-sm text-[#956C4A]">
                            {t("Play every day Miss a day, and it's gone forever")}
                        </p>
                    </div>

                    <div className="mt-5 flex justify-center">
                        <RamadanRiddlesCalendar days={ramadanDays} />
                    </div>

                    <RamadanRiddlesQuestionBanner
                        banner={questionBanner}
                        onStartQuestion={handleStartQuestion}
                    />

                    <div className="mt-6 grid grid-cols-2 gap-3">
                        <button
                            type="button"
                            onClick={() => setView("rewards")}
                            className="flex items-center justify-between rounded-md bg-[#FFF4D6] px-4 py-2 text-sm font-bold text-[#4F2F18]"
                        >
                            <span className="flex items-center gap-2">
                                <RiddlesRewardsIcon className="h-5 w-5 text-[#F27D23]" />
                                {t("My Rewards")}
                            </span>
                            <LeftArrow
                                className="h-4 w-4 rotate-180 text-[#B08A67] rtl:rotate-0"
                                aria-hidden="true"
                            />
                        </button>
                        <button
                            type="button"
                            onClick={() => setView("guide")}
                            className="flex items-center justify-between rounded-md bg-white px-4 py-2 text-sm font-bold text-[#4F2F18]"
                        >
                            <span className="flex items-center gap-2">
                                <RiddlesHelpIcon className="h-5 w-5 text-[#956C4A]" />
                                {t("How it works")}
                            </span>
                            <LeftArrow
                                className="h-4 w-4 rotate-180 text-[#B08A67] rtl:rotate-0"
                                aria-hidden="true"
                            />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RamadanRiddlesProgress;
